import React, { useEffect, useRef, useCallback, useState } from 'react';
import { VECTOR_ASCII, LABS_ASCII } from '../data/heroAscii';

/* ─── rainbow colour helpers ─── */
const RAINBOW = [
  '#ff0040', '#ff4000', '#ff8000', '#ffc000',
  '#ffff00', '#80ff00', '#00ff80', '#00ffff',
  '#0080ff', '#4000ff', '#8000ff', '#ff00ff',
];

function lerpHex(a, b, t) {
  const p = h => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
  const [r1, g1, b1] = p(a);
  const [r2, g2, b2] = p(b);
  return `rgb(${Math.round(r1 + (r2 - r1) * t)},${Math.round(g1 + (g2 - g1) * t)},${Math.round(b1 + (b2 - b1) * t)})`;
}

function getRainbowColor(pos) {
  const i = Math.floor(pos * (RAINBOW.length - 1));
  const t = (pos * (RAINBOW.length - 1)) - i;
  return lerpHex(RAINBOW[i], RAINBOW[Math.min(i + 1, RAINBOW.length - 1)], t);
}

/* Build colored spans — NO transitions (JS drives updates directly) */
function buildAsciiSpans(text) {
  const visible = text.replace(/[\s\n]/g, '').length;
  let ci = 0;
  return text.split('').map(ch => {
    if (ch === '\n') return '\n';
    if (ch === ' ') return ' ';
    const idx = ci++;
    const c = getRainbowColor(idx / visible);
    const safe = ch === '<' ? '&lt;' : ch === '>' ? '&gt;' : ch === '&' ? '&amp;' : ch;
    return `<span data-i="${idx}" style="color:${c}">${safe}</span>`;
  }).join('');
}

/* ─── Rotating tagline with human-like typewriter effect ─── */
const TAGLINES = [
  'Applied AI Research & Engineering',
  'AI Automation Systems',
  'Agent Workflows & Orchestration',
  'Developer Productivity Tools',
  'Local AI Infrastructure',
  'Data Pipelines & Analytics',
  'CRM & Operations Consulting',
  'Software Engineering Solutions',
];

const HOLD_DURATION = 2800;  // ms to hold the full phrase

/* Human-like per-keystroke delay: base + jitter + pauses after spaces/punctuation */
function humanDelay(ch, prevCh) {
  const base = 55 + Math.random() * 45;              // 55-100ms base
  if (prevCh === ' ') return base + 30 + Math.random() * 60;   // slight pause after space
  if ('&,.;:-'.includes(ch)) return base + 50 + Math.random() * 80; // hesitate on punctuation
  if (Math.random() < 0.08) return base + 120 + Math.random() * 100; // occasional longer pause (~8%)
  return base;
}

function eraseDelay() {
  return 25 + Math.random() * 30; // 25-55ms, quicker but still varied
}

function TaglineAnimated() {
  const [text, setText] = useState('');
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [phase, setPhase] = useState('typing'); // typing | holding | erasing

  useEffect(() => {
    const phrase = TAGLINES[phraseIdx];
    let timer;

    if (phase === 'typing') {
      if (text.length < phrase.length) {
        const nextCh = phrase[text.length];
        const prevCh = text.length > 0 ? phrase[text.length - 1] : '';
        const delay = humanDelay(nextCh, prevCh);
        timer = setTimeout(() => setText(phrase.slice(0, text.length + 1)), delay);
      } else {
        timer = setTimeout(() => setPhase('holding'), 0);
      }
    } else if (phase === 'holding') {
      timer = setTimeout(() => setPhase('erasing'), HOLD_DURATION);
    } else if (phase === 'erasing') {
      if (text.length > 0) {
        timer = setTimeout(() => setText(text.slice(0, -1)), eraseDelay());
      } else {
        setPhraseIdx((phraseIdx + 1) % TAGLINES.length);
        setPhase('typing');
      }
    }

    return () => clearTimeout(timer);
  }, [text, phase, phraseIdx]);

  // Generate rainbow colors for current text
  const colors = text.split('').map((_, i) =>
    getRainbowColor(i / Math.max(TAGLINES[phraseIdx].length, 1))
  );

  return (
    <p className="hero-ascii-tagline-text" aria-label={TAGLINES[phraseIdx]}>
      {text.split('').map((ch, i) => (
        <span
          key={i}
          className="tagline-char revealed"
          style={{ color: colors[i] }}
        >
          {ch === ' ' ? '\u00A0' : ch}
        </span>
      ))}
      <span className="tagline-cursor">|</span>
    </p>
  );
}

/* ─── Spatial grid for O(1)-ish proximity lookups ─── */
const RADIUS = 80;
const CELL = RADIUS; // grid cell size = radius for simple 2-cell neighborhood

function buildGrid(positions) {
  const grid = {};
  for (let i = 0; i < positions.length; i++) {
    const { x, y } = positions[i];
    const key = `${Math.floor(x / CELL)},${Math.floor(y / CELL)}`;
    (grid[key] || (grid[key] = [])).push(i);
  }
  return grid;
}

function queryGrid(grid, mx, my) {
  const results = [];
  const cx = Math.floor(mx / CELL);
  const cy = Math.floor(my / CELL);
  // Check 3x3 neighborhood of cells
  for (let dx = -1; dx <= 1; dx++) {
    for (let dy = -1; dy <= 1; dy++) {
      const bucket = grid[`${cx + dx},${cy + dy}`];
      if (!bucket) continue;
      for (let k = 0; k < bucket.length; k++) {
        results.push(bucket[k]);
      }
    }
  }
  return results;
}

export default function Hero() {
  const preRef = useRef(null);
  const sectionRef = useRef(null);

  // Perf refs — no state, no re-renders
  const spansRef = useRef([]);
  const posRef = useRef([]); // {x,y} per span (relative to <pre>)
  const gridRef = useRef({});
  const litRef = useRef(new Set()); // indices currently glowing
  const rafRef = useRef(null);
  const mouseRef = useRef({ x: -9999, y: -9999 });

  /* Render ASCII art once */
  useEffect(() => {
    if (!preRef.current) return;
    const combined = VECTOR_ASCII + '\n' + LABS_ASCII;
    preRef.current.innerHTML = buildAsciiSpans(combined);
    spansRef.current = Array.from(preRef.current.querySelectorAll('span[data-i]'));
    cachePositions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Cache all span positions (relative to pre element) — called on mount & resize */
  const cachePositions = useCallback(() => {
    const pre = preRef.current;
    if (!pre) return;
    const spans = spansRef.current;
    const rect = pre.getBoundingClientRect();
    const positions = new Array(spans.length);
    for (let i = 0; i < spans.length; i++) {
      const sr = spans[i].getBoundingClientRect();
      positions[i] = {
        x: sr.left - rect.left + sr.width / 2,
        y: sr.top - rect.top + sr.height / 2,
      };
    }
    posRef.current = positions;
    gridRef.current = buildGrid(positions);
  }, []);

  /* Recache on resize */
  useEffect(() => {
    let timeout;
    const onResize = () => {
      clearTimeout(timeout);
      timeout = setTimeout(cachePositions, 200);
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      clearTimeout(timeout);
    };
  }, [cachePositions]);

  /* Mouse proximity glow — only touches nearby spans via spatial grid */
  const handleMouseMove = useCallback((e) => {
    mouseRef.current = { x: e.clientX, y: e.clientY };

    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      const pre = preRef.current;
      if (!pre) return;

      const preRect = pre.getBoundingClientRect();
      const mx = mouseRef.current.x - preRect.left;
      const my = mouseRef.current.y - preRect.top;
      const spans = spansRef.current;
      const positions = posRef.current;
      const R2 = RADIUS * RADIUS;

      // Find candidates from spatial grid
      const candidates = queryGrid(gridRef.current, mx, my);
      const newLit = new Set();

      // Light up nearby chars
      for (let k = 0; k < candidates.length; k++) {
        const idx = candidates[k];
        const pos = positions[idx];
        const d2 = (mx - pos.x) ** 2 + (my - pos.y) ** 2;
        if (d2 < R2) {
          const intensity = 1 - Math.sqrt(d2) / RADIUS;
          const glow = 6 + intensity * 14;
          spans[idx].style.textShadow = `0 0 ${glow}px currentColor`;
          spans[idx].style.filter = `brightness(${1 + intensity * 0.8})`;
          newLit.add(idx);
        }
      }

      // Reset chars that were lit last frame but aren't anymore
      const oldLit = litRef.current;
      oldLit.forEach(idx => {
        if (!newLit.has(idx)) {
          spans[idx].style.textShadow = 'none';
          spans[idx].style.filter = '';
        }
      });

      litRef.current = newLit;
    });
  }, []);

  const handleMouseLeave = useCallback(() => {
    mouseRef.current = { x: -9999, y: -9999 };
    const spans = spansRef.current;
    litRef.current.forEach(idx => {
      spans[idx].style.textShadow = 'none';
      spans[idx].style.filter = '';
    });
    litRef.current.clear();
  }, []);

  return (
    <section
      className="hero-ascii"
      ref={sectionRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div className="hero-ascii-bg" />
      <div className="hero-ascii-content">
        <pre ref={preRef} className="hero-ascii-art" aria-label="VECTOR LABS" />
        <div className="hero-ascii-tagline">
          <TaglineAnimated />
          <div className="hero-ascii-actions">
            <a href="/products" className="hero-btn hero-btn-primary">Explore Our Work</a>
            <a href="/research" className="hero-btn hero-btn-ghost">Research Papers</a>
          </div>
        </div>
      </div>
    </section>
  );
}
